import { Copy } from "lucide-react";

import { useState } from "react";

import { DataConnector } from "@/api/data-connectors";
import { Card } from "@/design-system/components/Card";
import { Tabs } from "@/design-system/components/Tabs/Tabs";
import { toast } from "@/design-system/components/Toast/Toast";

interface Props {
  dataConnector: DataConnector;
}

export function ApiHelperCard({ dataConnector }: Props) {
  const [selectedExample, setSelectedExample] =
    useState<ApiExampleType>("identify");
  const currentExample = apiExampleOptions[selectedExample];

  return (
    <Card headerText="Send data via the Freshpaint API">
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium">Data Connector ID</p>
          <CopyableValue value={dataConnector.id} mono />
        </div>

        <Tabs.Root
          value={selectedExample}
          onValueChange={(v) => {
            setSelectedExample(v as ApiExampleType);
          }}
          size="small"
        >
          <Tabs.List>
            {Object.keys(apiExampleOptions).map((example) => (
              <Tabs.Trigger
                key={example}
                value={example}
                className="capitalize"
              >
                {example}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>

        <div>
          <p className="text-sm font-medium">Endpoint</p>
          <CopyableValue value={currentExample.endpoint} />
        </div>

        <div>
          <p className="text-sm font-medium">Sample Request</p>
          <CodeBlock value={currentExample.sampleRequest(dataConnector.id)} />
        </div>
      </div>
    </Card>
  );
}

type ApiExampleType = "identify";

const apiExampleOptions: Record<
  ApiExampleType,
  {
    endpoint: string;
    sampleRequest: (dataConnectorID: string) => string;
  }
> = {
  identify: {
    endpoint: "https://api.freshpaint.io/v1/identify",
    sampleRequest: (
      dataConnectorId: string,
    ) => `curl -X POST https://api.freshpaint.io/v1/identify \\
  -H "Authorization: Bearer <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "data_connector_id": "${dataConnectorId}",
    "records": [
      {
        "email": "a@example.com",
        "phone": "+15558675309",
        "distinct_id": "<arbitrary text, e.g. external id value, or email>",
        "device_id": "<device id from freshpaint sdk>",
        "plan_type": "Gold",
        "lead_score": 8,
        "service_line": "podiatry",
        "age": 27,
        "first_name": "John",
        "last_name": "Doe"
      }
    ]
}'

# Each record must include at least one identifier: email, phone,
# distinct_id, device_id.
# "distinct_id" is the canonical user identifier.`,
  },
};

function CopyableValue({ value, mono }: { value: string; mono?: boolean }) {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="border-borderSubtle bg-surface-sunken flex min-w-0 items-center gap-2 rounded-md border px-3 py-2">
      <span
        className={`min-w-0 flex-1 truncate text-xs ${mono ? "font-mono" : ""}`}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="text-textSubtle hover:text-textDefault flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded hover:bg-black/5"
        aria-label="Copy"
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

function CodeBlock({ value }: { value: string }) {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="border-borderSubtle bg-surface-sunken relative rounded-md border p-3">
      <button
        type="button"
        onClick={handleCopy}
        className="text-textSubtle hover:text-textDefault absolute top-2 right-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded hover:bg-black/5"
        aria-label="Copy code"
      >
        <Copy size={14} />
      </button>
      <pre className="text-textDefault overflow-x-auto pr-8 font-mono text-xs leading-5 whitespace-pre">
        {value}
      </pre>
    </div>
  );
}
